import React, { useContext, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { Spinner } from 'react-bootstrap';
import { AppContext } from '../../App';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';
import { catalogEmailSuccessMessage } from '../../utils/catalogQuoteEmailResponse';
import { supplierGroupHasRequestedOrders } from '../../utils/orderRequestCart';
import { requestQuotesForSupplierGroup } from '../../utils/orderRequestQuoteBatch';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';

const LOG_CONTEXT = 'OrderRequestSupplierGroupQuoteButton';

/**
 * Request supplier quotes for all open order requests in a supplier accordion group.
 *
 * @param {Object} props
 * @param {{ supplierId?: *, supplierName?: string, requests?: Array<Object> }} props.group
 * @param {boolean} props.hasGlobalContact
 * @param {string} [props.contactDisabledReason]
 * @param {function} [props.onConfigureContact]
 */
const OrderRequestSupplierGroupQuoteButton = ({
  group,
  hasGlobalContact,
  contactDisabledReason = '',
  onConfigureContact,
}) => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasOpenRequests = supplierGroupHasRequestedOrders(group);

  let disabledReason = contactDisabledReason;
  if (!disabledReason && !hasGlobalContact) {
    disabledReason = 'Configure lab contact before requesting quotes.';
  }
  if (!disabledReason && !hasOpenRequests) {
    disabledReason = 'No open (REQUESTED) order requests in this supplier group.';
  }

  const isDisabled = !!disabledReason || isSubmitting;

  const handleClick = async e => {
    e.stopPropagation();
    e.preventDefault();
    if (isDisabled || !token) return;

    setIsSubmitting(true);
    logInfo(LOG_CONTEXT, 'supplier group quote submit', {
      supplierId: group?.supplierId ?? null,
      supplierName: group?.supplierName ?? '',
    });

    const result = await requestQuotesForSupplierGroup(token, group);
    setIsSubmitting(false);

    if (result.success) {
      const msg = catalogEmailSuccessMessage(
        result.data ?? { corporate_demand_ref: '', suppliers: [] }
      );
      showToast(
        `${msg} (${result.requestCount} request(s) in ${group.supplierName || 'supplier'} group.)`,
        'success',
        'top-right',
        6000
      );
    } else {
      showToast(
        result.messages?.filter(Boolean).join(' ') ||
          'Could not send quote requests for this supplier group.',
        'error',
        'top-right',
        6000
      );
    }
  };

  if (isViewer || !hasOpenRequests) {
    return null;
  }

  return (
    <Button
      variant="primary"
      size="sm"
      disabled={isDisabled}
      title={disabledReason || undefined}
      onClick={handleClick}
      className="flex-shrink-0"
      onMouseDown={e => e.stopPropagation()}
    >
      {isSubmitting ? (
        <>
          <Spinner animation="border" size="sm" className="me-1" />
          Sending…
        </>
      ) : (
        'Request quotes'
      )}
    </Button>
  );
};

export default OrderRequestSupplierGroupQuoteButton;
